/**
 * Cross-tab refresh coordination (DNKADM-0497 — the front desk stays signed in).
 *
 * saas-auth rotates the refresh cookie on every `POST /api/auth/refresh`. Two tabs
 * (or a waking iPad) refreshing at once used to present the same cookie twice; the
 * loser was signed out. saas-auth (SASATH-0400) now answers that loser with
 * `401 REFRESH_TOKEN_SUPERSEDED` and revokes nothing. This module makes the app
 * side cooperate:
 *
 *  1. Every refresh runs inside the Web Lock `dink-admin-refresh`, so tabs refresh
 *     one at a time. Inside the lock, if ANOTHER tab of the same signed-in user
 *     refreshed in the last few seconds (announced over a same-origin
 *     `BroadcastChannel`), that result is reused instead of refreshing again.
 *     With no `navigator.locks` (Safari < 15.4, insecure context) there is no lock
 *     and no reuse: the refresh goes straight out, as before. Inside the lock the
 *     refresh is rebuilt before it is sent, so it carries the CSRF token that
 *     matches the cookie as it is NOW (each refresh rotates `journix_csrf`), and
 *     with Web Locks every CSRF read for a refresh happens inside the lock. The
 *     lock wait is bounded (`REFRESH_LOCK_WAIT_MS`); past it the refresh goes out
 *     unlocked rather than waiting on a holder that never finishes. The lock HOLD
 *     is bounded too (`REFRESH_LOCK_HOLD_MS`): a slow refresh is never aborted, but
 *     it releases the lock after the bound and its result is still used.
 *  2. A `401 REFRESH_TOKEN_SUPERSEDED` is never a sign-out: the refresh is sent
 *     once more (the cookie now holds the newer token). A second failure, or any
 *     other error, is returned unchanged, so the caller signs out as it always did.
 *
 * Tokens stay IN MEMORY ONLY. The broadcast carries the refresh result between
 * live same-origin tabs; nothing is written to localStorage, sessionStorage or a
 * cookie.
 *
 * This works at the axios ADAPTER level (`client.ts` installs it on the
 * `/api/auth/refresh` POST), so every refresh path — the vendored factory's
 * `runRefresh` and `bootRefresh` (AuthGate / AuthReturn cold boot) — goes through
 * it without editing the vendored `httpClientFactory.ts`. At adapter level the
 * response body is still the raw transport payload (axios parses JSON after the
 * adapter), so bodies are parsed defensively here.
 */
import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios';

export const REFRESH_LOCK_NAME = 'dink-admin-refresh';
export const REFRESH_CHANNEL_NAME = 'dink-admin-refresh';
export const REFRESH_SUPERSEDED_CODE = 'REFRESH_TOKEN_SUPERSEDED';
/** "Another tab refreshed in the last few seconds." */
export const REFRESH_REUSE_WINDOW_MS = 5_000;
/**
 * Longest a refresh waits for the lock. A holder that never finishes (a frozen
 * background tab, a half-up network) must not stall every tab: after this the
 * refresh goes out unlocked, as it did before the lock existed. Never a sign-out.
 */
export const REFRESH_LOCK_WAIT_MS = 10_000;
/**
 * Longest a refresh HOLDS the lock. A slow refresh is never aborted (today it
 * eventually succeeds; cutting it off would be a sign-out). Past this the lock is
 * released so other tabs proceed — their SUPERSEDED retry covers any overlap —
 * while this refresh keeps running and its result is still used.
 */
export const REFRESH_LOCK_HOLD_MS = 10_000;

const MESSAGE_TYPE = 'dink-admin-refreshed';

export interface RefreshResult {
  accessToken: string;
  expiresAt: string | null;
  csrfToken: string | null;
}

/** The slice of `navigator.locks` used here. */
export interface RefreshLocks {
  /** Rejects (AbortError) if `signal` aborts before the lock is granted. */
  request<T>(name: string, callback: () => Promise<T>, signal?: AbortSignal): Promise<T>;
}

/** The slice of `BroadcastChannel` used here. */
export interface RefreshChannel {
  postMessage(message: unknown): void;
  onmessage: ((event: { data: unknown }) => void) | null;
}

export interface RefreshCoordinatorDeps {
  /** Resolved per refresh; `null` = no Web Locks, today's unserialized behavior. */
  locks: () => RefreshLocks | null;
  /** Created lazily, only when locks exist; `null` = no reuse, lock only. */
  channel: () => RefreshChannel | null;
  /** Whether another tab's result belongs to this tab's signed-in user. */
  canReuse: (result: RefreshResult) => boolean;
  /** Lock-wait bound; default `REFRESH_LOCK_WAIT_MS`. */
  lockWaitMs?: () => number;
  /** Lock-hold bound; default `REFRESH_LOCK_HOLD_MS`. */
  lockHoldMs?: () => number;
  now?: () => number;
}

/** Sends one refresh. Resolves on 2xx; rejects like an axios adapter (an error with `response`) otherwise. */
export type RefreshSend = () => Promise<AxiosResponse>;

export interface RefreshCoordinator {
  /**
   * Run one refresh for `config`.
   *
   * `send` sends the request exactly as axios built it (the real transport adapter).
   * It is used only on the no-lock path, which is today's behavior.
   *
   * `sendFresh` builds and sends a NEW refresh request, so its CSRF token is read
   * at send time. saas-auth's refresh is CSRF double-submit protected and every
   * refresh rotates the `journix_csrf` cookie, so a request built before waiting
   * on the lock (or before a SUPERSEDED answer) carries a token that may no longer
   * match the cookie. Inside the lock, the first attempt and the single SUPERSEDED
   * retry therefore both use `sendFresh`.
   */
  run(config: InternalAxiosRequestConfig, send: RefreshSend, sendFresh: RefreshSend): Promise<AxiosResponse>;
  /** Forget any other tab's result (on sign-out). */
  clear(): void;
}

export function createRefreshCoordinator(deps: RefreshCoordinatorDeps): RefreshCoordinator {
  const now = deps.now ?? (() => Date.now());
  let fromOtherTab: { result: RefreshResult; at: number } | null = null;
  let channel: RefreshChannel | null | undefined;

  function getChannel(): RefreshChannel | null {
    if (channel !== undefined) return channel;
    channel = deps.channel();
    if (channel) {
      channel.onmessage = (event) => {
        const data = event.data as { type?: unknown; result?: unknown } | null;
        if (!data || data.type !== MESSAGE_TYPE) return;
        const result = toResult(data.result);
        if (result) fromOtherTab = { result, at: now() };
      };
    }
    return channel;
  }

  function reusable(): RefreshResult | null {
    if (!fromOtherTab) return null;
    if (now() - fromOtherTab.at > REFRESH_REUSE_WINDOW_MS) return null;
    return deps.canReuse(fromOtherTab.result) ? fromOtherTab.result : null;
  }

  function announce(res: AxiosResponse): void {
    const result = toResult(parseBody(res.data));
    if (!result) return;
    try {
      getChannel()?.postMessage({ type: MESSAGE_TYPE, result });
    } catch {
      /* a closed channel only loses the reuse optimisation */
    }
  }

  async function attempt(
    config: InternalAxiosRequestConfig,
    send: RefreshSend,
    retry: RefreshSend,
    withReuse: boolean,
  ): Promise<AxiosResponse> {
    try {
      const res = await send();
      if (withReuse) announce(res);
      return res;
    } catch (err) {
      if (!isSuperseded(err)) throw err;
      // Another tab won the rotation a moment ago. Prefer its announced result;
      // otherwise retry ONCE — the cookie now holds the newer token. A failure of
      // the retry propagates unchanged and the caller signs out as before.
      const shared = withReuse ? reusable() : null;
      if (shared) return synthesize(config, shared);
      const res = await retry();
      if (withReuse) announce(res);
      return res;
    }
  }

  return {
    async run(config, send, sendFresh) {
      const locks = deps.locks();
      if (!locks) return attempt(config, send, sendFresh, false);
      getChannel(); // start listening before the first refresh
      // Bounded wait (manual timer + AbortController: AbortSignal.timeout needs
      // Safari 16, Web Locks only 15.4).
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), deps.lockWaitMs?.() ?? REFRESH_LOCK_WAIT_MS);
      let work: Promise<AxiosResponse> | null = null;
      try {
        await locks.request(
          REFRESH_LOCK_NAME,
          async () => {
            clearTimeout(timer);
            const shared = reusable();
            // Built fresh: another refresh may have rotated the CSRF cookie while this one waited.
            work = shared ? Promise.resolve(synthesize(config, shared)) : attempt(config, sendFresh, sendFresh, true);
            // Hold the lock until the refresh settles, or at most the hold bound.
            await settledOrAfter(work, deps.lockHoldMs?.() ?? REFRESH_LOCK_HOLD_MS);
          },
          controller.signal,
        );
      } catch {
        // Not granted in time (or the lock API failed) — `work` never started.
      } finally {
        clearTimeout(timer);
      }
      if (work) return work;
      // The lock was not granted in time: go out unlocked, as before the lock
      // existed — still built fresh, since with locks present the pre-built request
      // carries no CSRF token.
      return attempt(config, sendFresh, sendFresh, false);
    },
    clear() {
      fromOtherTab = null;
    },
  };
}

/** Resolve when `promise` settles or after `ms`, whichever comes first. Never rejects. */
async function settledOrAfter(promise: Promise<unknown>, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      promise.then(
        () => undefined,
        () => undefined,
      ),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, ms);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/** True for a `401` whose saas-auth envelope (`{ error: { code } }`) says REFRESH_TOKEN_SUPERSEDED. */
export function isSuperseded(err: unknown): boolean {
  const response = (err as { response?: { status?: number; data?: unknown } } | null)?.response;
  if (!response || response.status !== 401) return false;
  const body = parseBody(response.data) as { error?: { code?: unknown } } | null;
  return body?.error?.code === REFRESH_SUPERSEDED_CODE;
}

/** Browser Web Locks, or `null` where the API is missing (today's behavior). */
export function browserRefreshLocks(): RefreshLocks | null {
  if (typeof navigator === 'undefined') return null;
  const locks = (navigator as { locks?: { request?: unknown } }).locks;
  if (!locks || typeof locks.request !== 'function') return null;
  const manager = locks as unknown as LockManager;
  return {
    request: <T>(name: string, callback: () => Promise<T>, signal?: AbortSignal) =>
      manager.request(
        name,
        signal ? { mode: 'exclusive', signal } : { mode: 'exclusive' },
        callback,
      ) as Promise<T>,
  };
}

/** A same-origin BroadcastChannel, or `null` where the API is missing. */
export function browserRefreshChannel(): RefreshChannel | null {
  if (typeof BroadcastChannel !== 'function') return null;
  try {
    const ch = new BroadcastChannel(REFRESH_CHANNEL_NAME);
    // Node's BroadcastChannel (test runs) would otherwise hold the process open.
    (ch as unknown as { unref?: () => void }).unref?.();
    return ch as unknown as RefreshChannel;
  } catch {
    return null;
  }
}

function synthesize(config: InternalAxiosRequestConfig, result: RefreshResult): AxiosResponse {
  return {
    data: { accessToken: result.accessToken, expiresAt: result.expiresAt, csrfToken: result.csrfToken },
    status: 200,
    statusText: 'OK',
    headers: {},
    config,
  };
}

function parseBody(data: unknown): unknown {
  if (typeof data !== 'string') return data ?? null;
  try {
    return JSON.parse(data) as unknown;
  } catch {
    return null;
  }
}

function toResult(value: unknown): RefreshResult | null {
  const v = value as { accessToken?: unknown; expiresAt?: unknown; csrfToken?: unknown } | null;
  if (!v || typeof v.accessToken !== 'string' || v.accessToken.length === 0) return null;
  return {
    accessToken: v.accessToken,
    expiresAt: typeof v.expiresAt === 'string' ? v.expiresAt : null,
    csrfToken: typeof v.csrfToken === 'string' ? v.csrfToken : null,
  };
}
